import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Typography, Badge, IconButton, CircularProgress } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import MyStethiAIIcon from 'src/components/Icons/MyStethiAIIcon'
import { setMobileDrawerState } from 'src/redux/aiCandidateSearch'

const CandidateChatBottomBar = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { chatHistory, mobileDrawerState, isAiTyping, typingStatus } = useSelector(state => state.aiCandidateSearch)

  const lastUserMessage = [...chatHistory].reverse().find(msg => msg.type === 'user')
  const messageCount = chatHistory.filter(msg => msg.type === 'user').length

  const handleExpand = () => {
    dispatch(setMobileDrawerState('expanded'))
  }

  const handleClose = () => {
    dispatch(setMobileDrawerState('hidden'))
  }

  if (mobileDrawerState !== 'minimized') {
    return null
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 52,
        backgroundColor: theme.palette.background.paper,
        borderTop: `2px solid ${theme.palette.ai.main}`,
        ...(isAiTyping && {
          '@keyframes borderPulse': {
            '0%, 100%': { borderTopColor: theme.palette.ai.main },
            '50%': { borderTopColor: theme.palette.ai.light },
          },
          animation: 'borderPulse 1.5s ease-in-out infinite',
        }),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        zIndex: 1300,
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(8px)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label="Expand chat"
        onClick={handleExpand}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleExpand() }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: 1,
          cursor: 'pointer',
          minWidth: 0,
        }}
      >
        <Badge
          badgeContent={messageCount}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.ai.main,
              fontSize: '0.65rem',
              height: 16,
              minWidth: 16,
            }
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: theme.palette.ai.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.1rem' }} />
          </Box>
        </Badge>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.ai.dark, fontSize: '0.8rem', lineHeight: 1.2 }}>
              Stethi
            </Typography>
            {isAiTyping && (
              <CircularProgress
                size={14}
                thickness={5}
                sx={{ color: theme.palette.ai.main }}
              />
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: isAiTyping ? theme.palette.ai.dark : theme.palette.text.secondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              fontSize: '0.7rem',
              fontWeight: isAiTyping ? 500 : 400,
            }}
          >
            {isAiTyping
              ? (typingStatus?.message || 'Searching...')
              : (lastUserMessage?.message || 'Start a conversation')
            }
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 0 }}>
        <IconButton
          size="small"
          onClick={handleExpand}
          sx={{
            color: theme.palette.ai.main,
            minWidth: 36,
            minHeight: 36,
            padding: 0.5,
          }}
        >
          <ExpandLessIcon sx={{ fontSize: '1.25rem' }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: theme.palette.grey[600],
            minWidth: 36,
            minHeight: 36,
            padding: 0.5,
          }}
        >
          <CloseIcon sx={{ fontSize: '1.25rem' }} />
        </IconButton>
      </Box>
    </Box>
  )
}

export default CandidateChatBottomBar
